import { pubsub } from '@services';
import { getElementDimensions } from '@utils';

class HeaderElement extends HTMLElement {
  #lastScrollPosition = window.scrollY;
  /** @type {import('@services').UnsubscribeFunction[]} */ #unsubscribers = [];

  connectedCallback() {
    this.#getAnnouncementBarHeight();
    this.#getHeaderHeight();
    this.#handleScroll();
    this.#updateHeaderTop();

    window.addEventListener('resize', this.#getHeaderHeight, { passive: true });
    window.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#unsubscribers.push(
      pubsub.subscribe('drawer:open', ({ id }) => {
        if (id !== 'menu-drawer') return;

        document.body.classList.add('header--drawer-open');
        this.#resetHeaderPosition();
      }),
      pubsub.subscribe('drawer:close', ({ id }) => {
        id === 'menu-drawer' && document.body.classList.remove('header--drawer-open');
      })
    );
  }

  disconnectedCallback() {
    document.body.classList.remove('header-is-oov', 'header--drawer-open');
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
    window.removeEventListener('resize', this.#getHeaderHeight);
    window.removeEventListener('scroll', this.#handleScroll);
  }

  #resetHeaderPosition() {
    this.#updateHeaderTop();
    this.#lastScrollPosition = window.scrollY;
  }

  /**
   * Set header height in global scope and CSS var
   */
  #getHeaderHeight = () => {
    const headerHeight = getElementDimensions('header-element').height;
    window.HEADER_HEIGHT = headerHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  };

  #getAnnouncementBarHeight = () => {
    if (!document.querySelector('announcement-bar')) return;

    const announcementBarHeight = getElementDimensions('announcement-bar').height;
    window.ANNOUNCEMENT_BAR_HEIGHT = announcementBarHeight;
    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${announcementBarHeight}px`
    );
  };

  /**
   * Set header top in global scope and CSS var
   */
  #updateHeaderTop = (top = 0) => {
    const headerTop = `${top}px`;
    document.documentElement.style.setProperty('--header-top', headerTop);
    document.body.classList.toggle('header-is-oov', top > 0);
  };

  /**
   * Handle scroll event
   */
  #handleScroll = () => {
    const currentScrollPosition = window.scrollY;

    if (document.body.classList.contains('header--drawer-open')) {
      this.#updateHeaderTop();
      this.#lastScrollPosition = currentScrollPosition;
      return;
    }

    if (currentScrollPosition <= window.HEADER_HEIGHT) {
      this.#updateHeaderTop();
      this.#lastScrollPosition = currentScrollPosition;
      return;
    }

    currentScrollPosition > this.#lastScrollPosition
      ? this.#updateHeaderTop(window.HEADER_HEIGHT)
      : this.#updateHeaderTop();

    this.#lastScrollPosition = currentScrollPosition;
  };
}

customElements.define('header-element', HeaderElement);
